import { Formik, FormikProps } from "formik";
import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { ErrorText } from "@/components/errors";
import { IConsultantProfile, IConsultantProfileLanguage } from "@/data/consultant-profiles";
import { Flex } from "@/eworkitComponents";
import { FormikForm } from "@/formikComponents";
import {
  FormikLanguagesSection,
  validationLanguagesSectionSchema,
} from "@/views/components/ConsultantProfile/Languages";
import { Messenger } from "@/views/components/Messenger";

import { profilePartialUpdaterBuilder } from "../profilePartialUpdaterBuilder";
import { IProfileFormikProps } from "../types";

export const validationLanguagesFormSchema = validationLanguagesSectionSchema;

export interface ILanguagesFormSectionValues {
  languages: IConsultantProfileLanguage[];
}

interface ILanguagesFormSectionProps {
  form: FormikProps<ILanguagesFormSectionValues>;
}

export const LANGUAGES_MESSENGER_CONTEXT = "profile-languages-form";

interface ILanguagesFormProps extends ILanguagesFormSectionProps, IEditableSectionRenderProps {
  source: IConsultantProfile;
}

export const LanguagesFormComponent: FC<ILanguagesFormSectionProps> = memo(({ form }) => {
  const { t } = useTranslation("session");

  return (
    <Flex direction="column" innerMargin="md">
      <Messenger context={LANGUAGES_MESSENGER_CONTEXT} />
      <Flex direction="column">
        <Flex direction="column" innerMargin="sm">
          {form.errors.languages && <ErrorText error={t("CONSULTANT_PROFILES.DETAIL.LANGUAGES.SENIORITY_ERROR")} />}
          <FormikLanguagesSection
            name="languages"
            testName="languages"
            value={form.values.languages}
            setFieldTouched={form.setFieldTouched}
          />
        </Flex>
      </Flex>
    </Flex>
  );
});

export const LanguagesForm: FC<ILanguagesFormProps> = memo(props => {
  const { form, editableProps } = props;
  useEditableSubmit(form, editableProps);

  return (
    <FormikForm>
      <LanguagesFormComponent form={form} />
    </FormikForm>
  );
});

interface ILanguagesFormikProps extends IProfileFormikProps<ILanguagesFormSectionValues>, ILanguagesFormProps {}

export const LanguagesFormik: FC<ILanguagesFormikProps> = memo(
  ({ initialValues, validationSchema, onSubmit, ...rest }) => (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {(form: FormikProps<ILanguagesFormSectionValues>) => <LanguagesForm {...rest} form={form} />}
    </Formik>
  ),
);

export const ConsultantProfileLanguagesEdit = profilePartialUpdaterBuilder(LanguagesFormik, {
  validationSchema: validationLanguagesFormSchema,
  messengerContext: LANGUAGES_MESSENGER_CONTEXT,
});
